import { useEffect, useRef, useState } from 'react';

export const useScrollOverflow = () => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const viewport = viewportRef.current;
      if (viewport) {
        const isOverflowing = viewport.scrollWidth > viewport.clientWidth;
        setShowScrollButtons(isOverflowing);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const scrollLeft = () => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return;
    }

    // Access tab children directly from the tab list
    const tabs = Array.from(tabList.children) as HTMLElement[];
    if (tabs.length === 0) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const viewportLeft = viewport.scrollLeft;
    const tolerance = 5;

    let targetTab: HTMLElement | null = null;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const tabLeft = tab.offsetLeft;

      if (tabLeft >= viewportLeft - tolerance) {
        if (i > 0) {
          targetTab = tabs[i - 1];
        }
        break;
      }
    }

    if (targetTab) {
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  const scrollRight = () => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return;
    }

    // Access tab children directly from the tab list
    const tabs = Array.from(tabList.children) as HTMLElement[];
    if (tabs.length === 0) {
      return;
    }

    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const viewportLeft = viewport.scrollLeft;
    const tolerance = 5;

    let targetTab: HTMLElement | null = null;

    for (const tab of tabs) {
      const tabLeft = tab.offsetLeft;

      if (tabLeft > viewportLeft + tolerance) {
        targetTab = tab;
        break;
      }
    }

    if (targetTab) {
      targetTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  return {
    viewportRef,
    tabListRef,
    showScrollButtons,
    scrollLeft,
    scrollRight,
  };
};
